import {
  Calendar as CalendarIcon,
  Check,
  CircleDot,
  FolderInput,
  GitBranch,
  ListChecks,
  MessageCircle,
  Paperclip,
  Pencil,
  Plus,
  Tag,
  Unlink,
  Upload,
  Users,
} from "lucide-react";
import { FranklinIcon } from "@/components/ui/franklin-icon";
import { StatusBadge } from "@/domains/pm/components/shared";
import { IssueDateCalendar } from "@/domains/pm/components/shared/IssueDateCalendar";
import { UserMultiSelectList } from "@/components/ui/user-multi-select";
import { DisciplineMultiSelectList } from "@/domains/pm/components/shared/DisciplineMultiSelectList";
import { useStatusCatalogue } from "@/domains/pm/statuses/statusCatalogueContext";
import { useStatusAdmin } from "@/domains/pm/statuses/statusAdminContext";
import { visibleStatuses } from "@/domains/pm/statuses/statusCatalogue";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { Discipline, IssueStatus, UserProfile } from "@/domains/pm/types";
import type { IssueMenuKit } from "./issueMenuKit";
import { ProjectStatusBadge } from "@/domains/projects/status/ProjectStatusBadge";
import { useProjectStatusEnabled } from "@/domains/projects/status/projectStatusAvailability";
import {
  partitionProjectsByStatus,
  shouldSplitByStatus,
  type ProjectStatus,
} from "@/domains/projects/status/projectStatus";

interface IssueMenuItemsProps {
  kit: IssueMenuKit;
  mode: "single" | "multi";
  /** Multi mode: number of selected issues, shown as a header. */
  selectedCount?: number;
  /** Single: the issue's status. Multi: undefined when the selection is mixed. */
  status?: IssueStatus;
  /** Single: the issue's assignees. Multi: ids common to every selected issue. */
  assigneeIds: number[];
  disciplineIds: number[];
  /** Single: the issue's start date. Multi: undefined (no common value). */
  startDate?: string | null;
  dueDate?: string | null;
  /** The building's projects, offered as move targets — inactive ones listed
   *  under their own label (a submenu has no room for a filter control). */
  projects: { id: number; name: string; status: ProjectStatus }[];
  /** Single: the issue's project. Multi: undefined when the selection spans
   *  more than one project — no project is then shown as the current one. */
  currentProjectId?: number;
  /** Single only — drives Make vs Remove subissue. */
  hasParent?: boolean;
  /** Single only — when true the issue already has subissues, so the
   *  "Mark as subissue" action is hidden entirely (WHA-3455). */
  hasSubissues?: boolean;
  users: UserProfile[];
  disciplines: Discipline[];
  onSetStatus: (s: IssueStatus) => void;
  onAssigneesChange: (ids: number[]) => void;
  onDisciplinesChange: (ids: number[]) => void;
  onSetStartDate: (d: string | null) => void;
  onSetDueDate: (d: string | null) => void;
  onMoveToProject: (projectId: number) => void;
  onAskFranklin: () => void;
  // Single-only actions.
  onChangeDescription?: () => void;
  onMakeSubissue?: () => void;
  onRemoveFromParent?: () => void;
  onCreateSubissue?: () => void;
  /** Omitted where no issue panel can open the checklist (the toolbar). */
  onAddChecklist?: () => void;
  onAddFromFranklin?: () => void;
  onUpload?: () => void;
}

const ICON = "mr-2 h-4 w-4 text-muted-foreground";

export function IssueMenuItems({
  kit: K,
  mode,
  selectedCount,
  status,
  assigneeIds,
  disciplineIds,
  startDate,
  dueDate,
  projects,
  currentProjectId,
  hasParent,
  hasSubissues,
  users,
  disciplines,
  onSetStatus,
  onAssigneesChange,
  onDisciplinesChange,
  onSetStartDate,
  onSetDueDate,
  onMoveToProject,
  onAskFranklin,
  onChangeDescription,
  onMakeSubissue,
  onRemoveFromParent,
  onCreateSubissue,
  onAddChecklist,
  onAddFromFranklin,
  onUpload,
}: IssueMenuItemsProps) {
  const { t } = useLanguage();
  const statusEnabled = useProjectStatusEnabled();
  const { catalogue: statusCatalogue } = useStatusCatalogue();
  // Null unless the page mounts the dialogs and the user may manage statuses.
  // The menu cannot mount its own: Radix unmounts the content on close, which
  // would take the dialog with it before it painted.
  const statusAdmin = useStatusAdmin();
  const cm = t.pm.issues.contextMenu;

  // In multi mode currentProjectId is undefined once the selection spans more
  // than one project; the union of every project's statuses is then offered,
  // and a status the target project does not define is refused by the trigger
  // with a named error rather than written.
  const buildingProjectId = currentProjectId ?? null;
  const statusOptions = visibleStatuses(statusCatalogue, buildingProjectId);

  // Status / Assignees / Discipline / Due date — shared by both modes.
  const projectItem = (p: { id: number; name: string; status: ProjectStatus }) => (
    <K.Item key={p.id} onSelect={() => onMoveToProject(p.id)}>
      <ProjectStatusBadge variant="dot" status={p.status} className="mr-2" />
      <span className="truncate">{p.name}</span>
      {p.id === currentProjectId && <Check className="ml-auto h-3.5 w-3.5" />}
    </K.Item>
  );
  const moveTargets = () => {
    // One list with a dot per row; grouped by status only past the threshold.
    const { active, inactive } = partitionProjectsByStatus(projects);
    if (!shouldSplitByStatus(projects.length, statusEnabled)) {
      return [...active, ...inactive].map(projectItem);
    }
    return (
      <>
        {active.length > 0 && (
          <>
            <K.Label className="text-xs font-normal text-muted-foreground">
              {t.projects.status.active}
            </K.Label>
            {active.map(projectItem)}
            {inactive.length > 0 && <K.Separator />}
          </>
        )}
        {inactive.length > 0 && (
          <>
            <K.Label className="text-xs font-normal text-muted-foreground">
              {t.projects.status.inactive}
            </K.Label>
            {inactive.map(projectItem)}
          </>
        )}
      </>
    );
  };

  const attributeSubs = (
    <>
      <K.Sub>
        <K.SubTrigger>
          <CircleDot className={ICON} />
          {cm.status}
        </K.SubTrigger>
        <K.SubContent>
          {statusOptions.map((option) => (
            <K.Item key={option.key} onSelect={() => onSetStatus(option.key)}>
              <StatusBadge status={option.key} buildingProjectId={buildingProjectId} />
              {option.key === status && <Check className="ml-auto h-3.5 w-3.5" />}
            </K.Item>
          ))}
          {/* Defining a status from here means you wanted to use it, so the
              issue takes it once the dialog saves. */}
          {statusAdmin && buildingProjectId != null && (
            <>
              <K.Separator />
              <K.Item onSelect={() => statusAdmin.createStatus(onSetStatus)}>
                <Plus className={ICON} />
                {t.pm.issues.statuses.newStatus}
              </K.Item>
            </>
          )}
        </K.SubContent>
      </K.Sub>

      <K.Sub>
        <K.SubTrigger>
          <Users className={ICON} />
          {cm.assignees}
        </K.SubTrigger>
        <K.SubContent className="w-64 p-0">
          {/* Stop keystrokes reaching the menu's typeahead while the user
              searches inside the embedded command list. */}
          <div onKeyDown={(e) => e.stopPropagation()}>
            <UserMultiSelectList
              users={users}
              selectedUserIds={assigneeIds}
              onChange={onAssigneesChange}
              searchPlaceholder={t.pm.issues.searchUsers}
              emptyMessage={t.pm.issues.noUsersFound}
            />
          </div>
        </K.SubContent>
      </K.Sub>

      <K.Sub>
        <K.SubTrigger>
          <Tag className={ICON} />
          {cm.discipline}
        </K.SubTrigger>
        <K.SubContent className="w-60 p-1">
          <DisciplineMultiSelectList
            disciplines={disciplines}
            selectedIds={disciplineIds}
            onChange={onDisciplinesChange}
            emptyMessage={t.pm.issues.noDiscipline}
          />
        </K.SubContent>
      </K.Sub>

      <K.Sub>
        <K.SubTrigger>
          <CalendarIcon className={ICON} />
          {cm.startDate}
        </K.SubTrigger>
        <K.SubContent className="p-0">
          <IssueDateCalendar
            field="start"
            startDate={startDate}
            dueDate={dueDate}
            onChange={onSetStartDate}
            clearLabel={cm.noStartDate}
          />
        </K.SubContent>
      </K.Sub>

      <K.Sub>
        <K.SubTrigger>
          <CalendarIcon className={ICON} />
          {cm.dueDate}
        </K.SubTrigger>
        <K.SubContent className="p-0">
          <IssueDateCalendar
            field="due"
            startDate={startDate}
            dueDate={dueDate}
            onChange={onSetDueDate}
            clearLabel={cm.noDueDate}
          />
        </K.SubContent>
      </K.Sub>

      <K.Sub>
        <K.SubTrigger>
          <FolderInput className={ICON} />
          {cm.moveToProject}
        </K.SubTrigger>
        <K.SubContent className="max-h-72 w-56 overflow-y-auto">
          {projects.length === 0 ? (
            <K.Label className="text-xs font-normal text-muted-foreground">
              {cm.noOtherProjects}
            </K.Label>
          ) : (
            moveTargets()
          )}
        </K.SubContent>
      </K.Sub>
    </>
  );

  if (mode === "multi") {
    return (
      <>
        <K.Label>{cm.selectedCount.replace("{count}", String(selectedCount ?? 0))}</K.Label>
        <K.Separator />
        {attributeSubs}
        <K.Separator />
        <K.Item onSelect={onAskFranklin}>
          <MessageCircle className={ICON} />
          {cm.askFranklin}
        </K.Item>
      </>
    );
  }

  return (
    <>
      <K.Item onSelect={() => onChangeDescription?.()}>
        <Pencil className={ICON} />
        {cm.changeDescription}
      </K.Item>
      <K.Separator />
      {attributeSubs}
      <K.Separator />
      {hasParent ? (
        <K.Item onSelect={() => onRemoveFromParent?.()}>
          <Unlink className={ICON} />
          {cm.removeFromParent}
        </K.Item>
      ) : hasSubissues ? null : (
        <K.Item onSelect={() => onMakeSubissue?.()}>
          <GitBranch className={ICON} />
          {cm.makeSubissue}
        </K.Item>
      )}
      <K.Item onSelect={() => onCreateSubissue?.()}>
        <Plus className={ICON} />
        {cm.createSubissue}
      </K.Item>
      {onAddChecklist && (
        <K.Item onSelect={onAddChecklist}>
          <ListChecks className={ICON} />
          {t.pm.issues.checklist.addChecklist}
        </K.Item>
      )}
      <K.Sub>
        <K.SubTrigger>
          <Paperclip className={ICON} />
          {cm.addDocument}
        </K.SubTrigger>
        <K.SubContent>
          <K.Item onSelect={() => onAddFromFranklin?.()}>
            <FranklinIcon className={ICON} />
            {t.pm.issues.detail.addFromFranklin}
          </K.Item>
          <K.Item onSelect={() => onUpload?.()}>
            <Upload className={ICON} />
            {t.pm.issues.detail.uploadFiles}
          </K.Item>
        </K.SubContent>
      </K.Sub>
      <K.Separator />
      <K.Item onSelect={onAskFranklin}>
        <MessageCircle className={ICON} />
        {cm.askFranklin}
      </K.Item>
    </>
  );
}
